import { Box, Card, Chip, Grid, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import GridContent from "../../../components/grid";
import { IHeaderGrid } from "../../../interfaces/grid/IHeaderGrid";
import GridHeader from "../../../components/grid/header";
import { useEffect, useState } from "react";
import useLazyLoading from "../../../common/hooks/loader";
import Loader from "../../../components/loader";
import { useNotification } from "../../../components/notification/useNotification";
import { IListPagination } from "../../../interfaces/pagination/IListPagination";
import { IProfitLossDto } from "../../../interfaces/dashboard/IProfitLossDto";
import { defaultControllerPagination, IPagination } from "../../../interfaces/pagination/IPagination";
import { tryGetProfitLossPaginatedDashboard } from "../../../services/dashboard/dashboard.service";
import extractErrorMessage from "../../../common/utils/errorMessages";
import Pagination from "../../../components/grid/pagination";
import { formatPriceToDolar } from "../../../common/utils/number";
import { colorByValue } from "../../../common/utils/color";
import ProfitLossFilters from "./filters";
import { useGlobalContext } from "../../../context/global-context";

const headerColumnList: IHeaderGrid[] = [
    {
        align: 'left',
        name: 'Cripto Moeda',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Nome Carteira',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Qtd. Moeda',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Total Investido',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Total Vendido',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Valor Actual',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Preço Médio',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Preço Actual',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Profit/Loss',
        padding: 'normal'
    },
    {
        align: 'right',
        name: 'Perc% Profit/Loss',
        padding: 'normal'
    },
];

export default function ProfitLossDashboard() {
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();
    const [profitLossList, setProfitLossList] = useState<IProfitLossDto[]>();
    const [rowCount, setRowCount] = useState<number>(0);
    const [pagination, setPagination] = useState<IPagination>(defaultControllerPagination);
    const { state } = useGlobalContext();
    const { profitLossFilters } = state;

    useEffect(() => {
        setLoading(true);
        getProfitLossDashboardPaginated();
        setLoading(false);
    }, [pagination, profitLossFilters]);

    async function getProfitLossDashboardPaginated() {
        try {
            var response = await tryGetProfitLossPaginatedDashboard(pagination, profitLossFilters);

            if (response?.status === 200) {
                setProfitLossList(response.data?.results);
                setRowCount(response.data?.rowCount);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    return (
        <>
            {showLoading ? <Loader /> : false}
            <Typography
                variant="h6"
                fontWeight={600}
                gutterBottom
                sx={{ color: 'text.primary', letterSpacing: 0.5 }}
            >
                Visão Analítica
            </Typography>
            <Box>
                <ProfitLossFilters />
            </Box>
            <Card sx={{ border: '1px solid rgba(255,215,0,0.1)' }}>
                <Box>
                    <GridContent>
                        <GridHeader headerProps={headerColumnList} />
                        <TableBody>
                            {profitLossList && profitLossList.map((profitLoss, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        backgroundColor: index % 2 !== 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,215,0,0.05)' },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <TableCell align="left">
                                        <Grid container alignItems="center" spacing={0.5}>
                                            <Grid>
                                                <img src={profitLoss.cryptoCurrencyUrl} width="18" height="18" />
                                            </Grid>
                                            <Grid>
                                                <strong>{profitLoss.cryptoCurrencyName}</strong>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{profitLoss.walletName}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{profitLoss.cryptoCurrencyQuantity}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{formatPriceToDolar(profitLoss.totalInvested)}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{formatPriceToDolar(profitLoss.totalSelled)}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{formatPriceToDolar(profitLoss.actualAmount)}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{formatPriceToDolar(profitLoss.averagePrice)}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography>{formatPriceToDolar(profitLoss.actualPrice)}</Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography color={colorByValue(profitLoss.profitLoss)}>
                                            {formatPriceToDolar(profitLoss.profitLoss)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={`${profitLoss.profitLossPercentage}%`}
                                            size="small"
                                            color={colorByValue(profitLoss.profitLossPercentage) as 'success' | 'error'}
                                            variant="outlined"
                                            sx={{ fontWeight: 600, fontSize: '0.72rem', borderRadius: '6px' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </GridContent>
                    <Pagination rowCount={rowCount} pagination={pagination} setController={setPagination} />
                </Box>
            </Card>
        </>
    );
}